// src/pages/Admin/pages/Users/UserDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    UserCheck,
    UserX,
    Shield,
    Mail,
    Phone,
    Calendar,
    Key,
    Eye,
    Building,
    Briefcase,
    CheckCircle,
    XCircle,
    Clock,
    EyeOff,
    TrendingDown,
    RefreshCw,
    CreditCard,
    TrendingUp,
    Package,
    PlusCircle,
    MinusCircle,
    Plus,
    Minus,
    ArrowRight,
    AlertTriangle,
    X,
    Wallet
} from 'lucide-react';
import userService from '../../../../services/userService';
import './UserDetail.css';

const UserDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<any>(null);
    const [credit, setCredit] = useState<any>(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showCreditModal, setShowCreditModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [creditLoading, setCreditLoading] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [creditAmount, setCreditAmount] = useState("");
    const [creditAction, setCreditAction] = useState('add'); // 'add' or 'deduct'
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordFocus, setPasswordFocus] = useState(false);
    const [confirmPasswordFocus, setConfirmPasswordFocus] = useState(false);
    const [alert, setAlert] = useState<{
        type: 'error' | 'success' | 'warning';
        title: string;
        message: string;
    } | null>(null);

    const [confirm, setConfirm] = useState<{
        title: string;
        message: string;
        onConfirm: () => void;
    } | null>(null);

    useEffect(() => {
        fetchUser();
        getCredit();
    }, [id]);

    const getCredit = async () => {
        try {
            setCreditLoading(true);
            const response = await userService.getCredit(parseInt(id!));
            setCredit(response.data);
        } catch (error) {
            console.error('Error fetching user credit:', error);
        } finally {
            setCreditLoading(false);
        }
    };

    const fetchUser = async () => {
        try {
            setLoading(true);
            const response = await userService.getUserById(parseInt(id!));
            setUser(response.data);
        } catch (error) {
            console.error('Error fetching user:', error);
            navigate('/admin/users-list');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (action: 'activate' | 'deactivate' | 'suspend') => {
        const actionText = {
            activate: 'activate',
            deactivate: 'deactivate',
            suspend: 'suspend'
        }[action];

        setConfirm({
            title: 'Confirm Status Change',
            message: `Are you sure you want to ${actionText} this user?`,
            onConfirm: async () => {
                try {
                    setActionLoading(true);

                    if (action === 'activate') {
                        await userService.activateUser(Number(id));
                    } else if (action === 'deactivate') {
                        await userService.deactivateUser(Number(id));
                    } else {
                        await userService.suspendUser(Number(id));
                    }

                    await fetchUser();
                    setConfirm(null);
                } catch (error) {
                    setAlert({
                        type: 'error',
                        title: 'Status Update Failed',
                        message: `Failed to ${actionText} user`
                    });
                } finally {
                    setActionLoading(false);
                }
            }
        });
    };


    const performCreditAction = async () => {
        try {
            setActionLoading(true);

            await userService.updateUserCredits(parseInt(id!), {
                amount: Number(creditAmount),
                action: creditAction
            });

            setAlert({
                type: 'success',
                title: 'Success',
                message: `Credits ${creditAction === 'add' ? 'added' : 'deducted'} successfully.`
            });

            setShowCreditModal(false);
            setCreditAmount("");
            getCredit();

        } catch (error) {
            console.error('Error updating credits:', error);
            setAlert({
                type: 'error',
                title: 'Credit Update Failed',
                message: 'Failed to update credits. Please try again.'
            });
        } finally {
            setActionLoading(false);
            setConfirm(null);
        }
    };

    const validatePassword = (password: string) => {
        const requirements = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password)
        };
        return {
            ...requirements,
            isValid: Object.values(requirements).every(req => req)
        };
    };

    const handlePasswordReset = async () => {
        if (!newPassword || !confirmPassword) {
            setAlert({
                type: 'error',
                title: 'Password Reset Failed',
                message: 'Please fill in both password fields'
            });

            return;
        }

        const passwordValidation = validatePassword(newPassword);
        if (!passwordValidation.isValid) {
            setAlert({
                type: 'error',
                title: 'Password Reset Failed',
                message: 'Password does not meet all requirements'
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            setAlert({
                type: 'error',
                title: 'Password Reset Failed',
                message: 'Passwords do not match'
            });
            return;
        }

        try {
            setActionLoading(true);
            await userService.resetPassword(parseInt(id!), {
                new_password: newPassword,
                confirm_password: confirmPassword
            });
            setAlert({
                type: 'success',
                title: 'Password Reset Successful',
                message: 'Password reset successfully!'
            });
            setShowPasswordModal(false);
            setNewPassword('');
            setConfirmPassword('');
            setShowPassword(false);
            setShowConfirmPassword(false);
        } catch (error) {
            console.error('Error resetting password:', error);
            setAlert({
                type: 'error',
                title: 'Password Reset Failed',
                message: 'Failed to reset password'
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleCreditAction = () => {
        const amount = Number(creditAmount);

        if (!amount || amount <= 0) {
            setAlert({
                type: 'error',
                title: 'Invalid Amount',
                message: 'Please enter a valid credit amount greater than zero.'
            });
            return;
        }

        setConfirm({
            title: 'Confirm Credit Action',
            message: `Are you sure you want to ${creditAction} ${amount} credits to this user?`,
            onConfirm: performCreditAction
        });
    };


    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getUserTypeIcon = () => {
        switch (user?.user_type) {
            case 'internal': return <Building size={18} />;
            case 'dccp': return <Shield size={18} />;
            case 'spokesly': return <Briefcase size={18} />;
            default: return null;
        }
    };

    const getStatusIcon = () => {
        switch (user?.account_status) {
            case 'active': return <CheckCircle size={18} className="text-green-600" />;
            case 'inactive': return <XCircle size={18} className="text-gray-600" />;
            case 'suspended': return <Shield size={18} className="text-red-600" />;
            case 'pending': return <Clock size={18} className="text-yellow-600" />;
            default: return null;
        }
    };

    const calculateUtilizationRate = (): string => {
        if (!credit || credit.total_credits === 0) return "0";
        return ((credit.used_credits / credit.total_credits) * 100).toFixed(1);
    };


    const passwordValidation = validatePassword(newPassword);

    if (loading) {
        return (
            <div className="user-detail-loading">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading user details...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return (

            <div className="user-not-found">
                <div className="not-found-content">
                    <XCircle size={48} className="text-red-600" />
                    <h2>User Not Found</h2>
                    <p>The user you're looking for doesn't exist.</p>
                    <button className="btn btn-primary" onClick={() => navigate('/admin/users-list')}>
                        <ArrowLeft size={18} /> Back to Users
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="user-detail-container">
                {/* Header */}
                <div className="user-detail-header">
                    <button className="btn-back" onClick={() => navigate('/admin/users-list')}>
                        <ArrowLeft size={20} />
                        <span>Back to Users</span>
                    </button>

                    <div className="header-actions">
                        <button
                            className="btn btn-outline"
                            onClick={() => setShowPasswordModal(true)}
                        >
                            <Key size={18} /> Reset Password
                        </button>
                        <button
                            className="btn btn-outline"
                            onClick={() => setShowCreditModal(true)}
                        >
                            <CreditCard size={18} /> Manage Credits
                        </button>
                        <button
                            className="btn btn-outline"
                            onClick={getCredit}
                            disabled={creditLoading}
                        >
                            <RefreshCw size={18} className={creditLoading ? 'spinning' : ''} /> Refresh Credit
                        </button>
                    </div>
                </div>

                {/* User Profile */}
                <div className="user-profile-card">
                    <div className="profile-header">
                        <div className="avatar">
                            {user.first_name?.[0]?.toUpperCase()}
                        </div>
                        <div className="profile-info">
                            <h2>{user.first_name} {user.last_name}</h2>
                            <div className="profile-meta">
                                <span className="meta-item">
                                    <Mail size={16} /> {user.email}
                                </span>
                                {user.phone_number && (
                                    <span className="meta-item">
                                        <Phone size={16} /> {user.phone_number}
                                    </span>
                                )}
                                {credit && (
                                    <span className="meta-item">
                                        <Wallet size={16} /> {credit.available_credits} credits available
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="status-actions">
                            <div className="current-status">
                                {getStatusIcon()}
                                <span className="status-text">
                                    {user.account_status.charAt(0).toUpperCase() + user.account_status.slice(1)}
                                </span>
                            </div>

                            <div className="action-buttons">
                                {user.account_status !== 'active' && (
                                    <button
                                        className="btn btn-success btn-sm"
                                        onClick={() => handleStatusChange('activate')}
                                        disabled={actionLoading}
                                    >
                                        <UserCheck size={16} /> Activate
                                    </button>
                                )}

                                {user.account_status === 'active' && (
                                    <button
                                        className="btn btn-warning btn-sm"
                                        onClick={() => handleStatusChange('deactivate')}
                                        disabled={actionLoading}
                                    >
                                        <UserX size={16} /> Deactivate
                                    </button>
                                )}

                                <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleStatusChange('suspend')}
                                    disabled={actionLoading}
                                >
                                    <Shield size={16} /> Suspend
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* User Details Grid */}
                    <div className="user-details-grid">
                        <div className="detail-section">
                            <h3>Basic Information</h3>
                            <div className="detail-list">
                                <div className="detail-item">
                                    <span className="label">Full Name:</span>
                                    <span className="value">{user.first_name} {user.last_name}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Email:</span>
                                    <span className="value">{user.email}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Phone:</span>
                                    <span className="value">{user.phone_number || 'Not provided'}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Job Title:</span>
                                    <span className="value">{user.job_title || 'Not specified'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="detail-section">
                            <h3>Account Information</h3>
                            <div className="detail-list">
                                <div className="detail-item">
                                    <span className="label">User Type:</span>
                                    <span className="value badge user-type">
                                        {getUserTypeIcon()}
                                        {user.user_type.charAt(0).toUpperCase() + user.user_type.slice(1)}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Role:</span>
                                    <span className="value badge role">{user.role_name}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Account Status:</span>
                                    <span className={`value badge status-${user.account_status}`}>
                                        {user.account_status.charAt(0).toUpperCase() + user.account_status.slice(1)}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Shift:</span>
                                    <span className="value">
                                        {user.shift === 'D' ? 'Day Shift' :
                                            user.shift === 'N' ? 'Night Shift' :
                                                user.shift === 'F' ? 'Flexible' : 'Not specified'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="detail-section">
                            <h3>Credit Information</h3>
                            <div className="detail-list">
                                <div className="detail-item">
                                    <span className="label">Total Credits:</span>
                                    <span className="value credit-total">
                                        <Wallet size={14} /> {credit?.total_credits || 0}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Used Credits:</span>
                                    <span className="value credit-used">
                                        <TrendingUp size={14} /> {credit?.used_credits || 0}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Available Credits:</span>
                                    <span className="value credit-available">
                                        <TrendingDown size={14} /> {credit?.available_credits || 0}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Utilization Rate:</span>
                                    <span className={`value ${parseFloat(calculateUtilizationRate()) > 80 ? 'text-warning' : 'text-success'}`}>
                                        {calculateUtilizationRate()}%
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="detail-section">
                            <h3>Activity Information</h3>
                            <div className="detail-list">
                                <div className="detail-item">
                                    <span className="label">Account Created:</span>
                                    <span className="value">
                                        <Calendar size={14} /> {formatDate(user.created_at)}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Last Updated:</span>
                                    <span className="value">
                                        <Calendar size={14} /> {formatDate(user.updated_at)}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Last Login:</span>
                                    <span className="value">
                                        {user.last_login_at ? (
                                            <>
                                                <Calendar size={14} /> {formatDate(user.last_login_at)}
                                            </>
                                        ) : 'Never logged in'}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Active:</span>
                                    <span className={`value ${user.is_active ? 'text-success' : 'text-danger'}`}>
                                        {user.is_active ? 'Yes' : 'No'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Credit Details Section */}
                        {/* <div className="detail-section credit-details-section">
            <h3>Credit Details</h3>
            {creditLoading ? (
              <div className="credit-loading">
                <div className="mini-spinner"></div>
                <span>Loading credit information...</span>
              </div>
            ) : credit ? (
              <div className="credit-details">
                <div className="credit-stats">
                  <div className="credit-stat-card total">
                    <div className="stat-icon">
                      <DollarSign size={20} />
                    </div>
                    <div className="stat-content">
                      <span className="stat-label">Total Credits</span>
                      <span className="stat-value">{credit.total_credits}</span>
                    </div>
                  </div>
                  
                  <div className="credit-stat-card used">
                    <div className="stat-icon">
                      <TrendingUp size={20} />
                    </div>
                    <div className="stat-content">
                      <span className="stat-label">Used Credits</span>
                      <span className="stat-value">{credit.used_credits}</span>
                    </div>
                  </div>
                  
                  <div className="credit-stat-card available">
                    <div className="stat-icon">
                      <TrendingDown size={20} />
                    </div>
                    <div className="stat-content">
                      <span className="stat-label">Available Credits</span>
                      <span className="stat-value">{credit.available_credits}</span>
                    </div>
                  </div>
                </div>
                
                <div className="credit-meta">
                  <div className="meta-item">
                    <span className="meta-label">Valid From:</span>
                    <span className="meta-value">{formatDate(credit.valid_from)}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Valid Until:</span>
                    <span className="meta-value">{formatDate(credit.valid_until)}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Status:</span>
                    <span className={`meta-value badge ${credit.is_active ? 'status-active' : 'status-inactive'}`}>
                      {credit.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {credit.payment_type && (
                    <div className="meta-item">
                      <span className="meta-label">Payment Type:</span>
                      <span className="meta-value">{credit.payment_type}</span>
                    </div>
                  )}
                  {credit.billing_period && (
                    <div className="meta-item">
                      <span className="meta-label">Billing Period:</span>
                      <span className="meta-value">{credit.billing_period}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="no-credit-info">
                <DollarSign size={24} className="text-muted" />
                <p>No credit information available</p>
                <button className="btn btn-sm btn-outline" onClick={() => setShowCreditModal(true)}>
                  <CreditCard size={14} /> Add Credits
                </button>
              </div>
            )}
          </div> */}

                        <div className="detail-section">
                            <h3>Security Information</h3>
                            <div className="detail-list">
                                <div className="detail-item">
                                    <span className="label">MAC Auth:</span>
                                    <span className={`value ${user.is_mac_auth_active ? 'text-success' : 'text-muted'}`}>
                                        {user.is_mac_auth_active ? 'Enabled' : 'Disabled'}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">MAC Address:</span>
                                    <span className="value">{user.mac_address || 'Not specified'}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Geographic Address:</span>
                                    <span className="value">{user.geo_address || 'Not specified'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Password Reset Modal */}
                {showPasswordModal && (
                    <div className="modal-overlay">
                        <div className="modal modal-password">
                            <div className="modal-header">
                                <h3>Reset Password</h3>
                                <button
                                    className="modal-close"
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setNewPassword('');
                                        setConfirmPassword('');
                                        setShowPassword(false);
                                        setShowConfirmPassword(false);
                                        setPasswordFocus(false);
                                        setConfirmPasswordFocus(false);
                                    }}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <p className="modal-description">
                                    Reset password for <strong>{user.first_name} {user.last_name}</strong>
                                </p>

                                <div className="form-group">
                                    <label>New Password</label>
                                    <div className="password-input-wrapper">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            onFocus={() => setPasswordFocus(true)}
                                            onBlur={() => setPasswordFocus(false)}
                                            placeholder="Enter new password"
                                            className="form-input password-input"
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>

                                    {/* Password Requirements Popup */}
                                    {(passwordFocus) && (
                                        <div className="password-requirements-popup">
                                            <p className="requirement-title">Password Requirements:</p>
                                            <ul className="requirement-list">
                                                <li className={passwordValidation.length ? 'requirement-met' : 'requirement-unmet'}>
                                                    <span className="requirement-icon">
                                                        {passwordValidation.length ? '✓' : '○'}
                                                    </span>
                                                    At least 8 characters
                                                </li>
                                                <li className={passwordValidation.uppercase ? 'requirement-met' : 'requirement-unmet'}>
                                                    <span className="requirement-icon">
                                                        {passwordValidation.uppercase ? '✓' : '○'}
                                                    </span>
                                                    One uppercase letter
                                                </li>
                                                <li className={passwordValidation.lowercase ? 'requirement-met' : 'requirement-unmet'}>
                                                    <span className="requirement-icon">
                                                        {passwordValidation.lowercase ? '✓' : '○'}
                                                    </span>
                                                    One lowercase letter
                                                </li>
                                                <li className={passwordValidation.number ? 'requirement-met' : 'requirement-unmet'}>
                                                    <span className="requirement-icon">
                                                        {passwordValidation.number ? '✓' : '○'}
                                                    </span>
                                                    One number
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Confirm Password</label>
                                    <div className="password-input-wrapper">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            onFocus={() => setConfirmPasswordFocus(true)}
                                            onBlur={() => setTimeout(() => setConfirmPasswordFocus(false), 200)}
                                            placeholder="Confirm new password"
                                            className="form-input password-input"
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        >
                                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>

                                    {/* Password Match Indicator */}
                                    {confirmPasswordFocus && newPassword && confirmPassword && (
                                        <div className={`password-match-indicator ${newPassword === confirmPassword ? 'match' : 'no-match'}`}>
                                            {newPassword === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    className="btn btn-outline"
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setNewPassword('');
                                        setConfirmPassword('');
                                        setShowPassword(false);
                                        setShowConfirmPassword(false);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handlePasswordReset}
                                    disabled={actionLoading || !newPassword || !confirmPassword || !passwordValidation.isValid || newPassword !== confirmPassword}
                                >
                                    {actionLoading ? (
                                        <>
                                            <span className="spinner-small"></span> Resetting...
                                        </>
                                    ) : 'Reset Password'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Credit Management Modal */}
                {showCreditModal && (
                    <div className="modal-overlay">
                        <div className="modal modal-credit">
                            {/* Professional Header */}
                            <div className="modal-header modal-header-professional">
                                <div className="header-content">
                                    <div className="header-icon">
                                        <CreditCard size={24} />
                                    </div>
                                    <div>
                                        <h3>Manage Credits</h3>
                                        <p className="header-subtitle">
                                            Adjust credits for <strong>{user.first_name} {user.last_name}</strong>
                                        </p>
                                    </div>
                                </div>
                                <button
                                    className="modal-close modal-close-clean"
                                    onClick={() => {
                                        setShowCreditModal(false);
                                        setCreditAmount("");
                                    }}
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="modal-body">
                                {/* Credit Summary Card */}
                                <div className="credit-summary-card">
                                    <div className="summary-header">
                                        <h4>Current Credit Status</h4>
                                        <span className={`status-badge ${credit?.is_active ? 'active' : 'inactive'}`}>
                                            {credit?.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>

                                    <div className="summary-grid">
                                        <div className="summary-item available">
                                            <div className="item-icon">
                                                <Wallet size={20} />
                                            </div>
                                            <div className="item-content">
                                                <span className="item-label">Available</span>
                                                <span className="item-value">{credit?.available_credits || 0}</span>
                                            </div>
                                        </div>

                                        <div className="summary-divider"></div>

                                        <div className="summary-item used">
                                            <div className="item-icon">
                                                <TrendingUp size={20} />
                                            </div>
                                            <div className="item-content">
                                                <span className="item-label">Used</span>
                                                <span className="item-value">{credit?.used_credits || 0}</span>
                                            </div>
                                        </div>

                                        <div className="summary-divider"></div>

                                        <div className="summary-item total">
                                            <div className="item-icon">
                                                <Package size={20} />
                                            </div>
                                            <div className="item-content">
                                                <span className="item-label">Total</span>
                                                <span className="item-value">{credit?.total_credits || 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <label className="form-label">Action Type</label>
                                    <div className="action-toggle-group">
                                        <button
                                            className={`action-toggle ${creditAction === 'add' ? 'active add-active' : ''}`}
                                            onClick={() => setCreditAction('add')}
                                        >
                                            <PlusCircle size={18} />
                                            <span>Add Credits</span>
                                        </button>

                                        <button
                                            className={`action-toggle ${creditAction === 'deduct' ? 'active deduct-active' : ''}`}
                                            onClick={() => setCreditAction('deduct')}
                                        >
                                            <MinusCircle size={18} />
                                            <span>Deduct Credits</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <label className="form-label">
                                        Credit
                                        <span className="required-indicator">*</span>
                                    </label>
                                    <div className="amount-input-container">
                                        <div className="input-prefix">
                                            <Wallet size={18} />
                                        </div>
                                        <input
                                            type="number"
                                            value={creditAmount}
                                            onChange={(e) => setCreditAmount(e.target.value)}
                                            placeholder="Enter Credit"
                                            className="form-input-styled"
                                            min="1"
                                            step="1"
                                        />
                                        <div className="input-suffix">
                                            credits
                                        </div>
                                    </div>
                                    <div className="input-hint">
                                        {Number(creditAmount) > 0 && (
                                            <span className={`hint-text ${creditAction === 'add' ? 'text-add' : 'text-deduct'}`}>
                                                {creditAction === 'add' ? 'Adding' : 'Deducting'} {creditAmount} credits
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="preview-section">
                                    <div className="preview-card">
                                        <div className="preview-header">
                                            <h4>Preview Changes</h4>
                                            <ArrowRight size={18} />
                                        </div>

                                        <div className="preview-content">
                                            <div className="preview-row">
                                                <span>Current Available</span>
                                                <span className="current-amount">{credit?.available_credits || 0}</span>
                                            </div>

                                            <div className="preview-row">
                                                <span>{creditAction === 'add' ? 'Addition' : 'Deduction'}</span>
                                                <span className={`adjustment-amount ${creditAction === 'add' ? 'positive' : 'negative'}`}>
                                                    {creditAction === 'add' ? '+' : '-'}{creditAmount}
                                                </span>
                                            </div>

                                            <div className="preview-row total-row">
                                                <span>New Available Balance</span>
                                                <span className="new-balance">
                                                    {(() => {
                                                        const current = Number(credit?.available_credits || 0);
                                                        const amount = Number(creditAmount || 0);

                                                        return creditAction === 'add'
                                                            ? current + amount
                                                            : Math.max(0, current - amount);
                                                    })()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {creditAction === 'deduct' && creditAmount > (credit?.available_credits || 0) && (
                                        <div className="warning-banner">
                                            <AlertTriangle size={18} />
                                            <div className="warning-content">
                                                <strong>Insufficient Credits:</strong> User only has {credit?.available_credits || 0} credits available.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="modal-footer professional-footer">
                                <button
                                    className="btn btn-ghost"
                                    onClick={() => {
                                        setShowCreditModal(false);
                                        setCreditAmount("");
                                    }}
                                >
                                    Cancel
                                </button>
                                <div className="footer-actions">
                                    <button
                                        className={`btn btn-action ${creditAction === 'add' ? 'btn-primary' : 'btn-warning'}`}
                                        onClick={handleCreditAction}
                                        disabled={actionLoading || Number(creditAmount) <= 0 || (creditAction === 'deduct' && Number(creditAmount) > (credit?.available_credits || 0))}
                                    >
                                        {actionLoading ? (
                                            <>
                                                <span className="spinner-mini"></span>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                {creditAction === 'add' ? (
                                                    <>
                                                        <Plus size={18} />
                                                        Add {creditAmount} Credits
                                                    </>
                                                ) : (
                                                    <>
                                                        <Minus size={18} />
                                                        Deduct {creditAmount} Credits
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {alert && (
                <div className="modal-overlay">
                    <div className={`modal modal-alert `} style={{height:"auto"}}>
                    {/* <div className="modal-header">
                        <h3>{alert.title}</h3>
                        <button
                            className="modal-close"
                            onClick={() => setAlert(null)}
                        >
                            <X size={18} />
                        </button>
                    </div> */}

                    <div className="modal-body">
                        <p>{alert.message}</p>
                    </div>

                    <div className="modal-footer">
                        <button
                            className="btn btn-primary"
                            onClick={() => setAlert(null)}
                        >
                            OK
                        </button>
                    </div>
                </div>
                </div >
            )}

{
    confirm && (
        <div className="modal-overlay">
            <div className="modal modal-confirm" style={{ height: "auto" }}>
                {/* <div className="modal-header">
                            <h3>{confirm.title}</h3>
                            <button
                                className="modal-close"
                                onClick={() => setConfirm(null)}
                            >
                                <X size={18} />
                            </button>
                        </div> */}

                <div className="modal-body">
                    <p>{confirm.message}</p>
                </div>

                <div className="modal-footer">
                    <button
                        className="btn btn-ghost"
                        onClick={() => setConfirm(null)}
                    >
                        Cancel
                    </button>
                    <button
                        className={`btn ${creditAction === 'add' ? 'btn-primary' : 'btn-warning'}`}
                        onClick={confirm.onConfirm}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Processing...' : 'Confirm'}
                    </button>
                </div>
            </div>
        </div>
    )
}

        </>
    );
};

export default UserDetail;